/**
 * Text-to-Speech service.
 * Server-side OpenAI TTS via /webchat/tts (supports Hebrew natively).
 */
import { store } from '../state/store';
import { prepareTextForTts } from '../utils/speechPunctuation';
import { applyAudioOutput } from './audioOutput';

export interface TtsCallbacks {
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (error: string) => void;
}

export interface TtsService {
  isSupported(): boolean;
  speak(text: string, lang: string, callbacks?: TtsCallbacks): void;
  startStreaming(lang: string, callbacks?: TtsCallbacks): void;
  appendStreamingText(text: string): void;
  finishStreaming(): void;
  hasActiveStream(): boolean;
  stop(): void;
  isSpeaking(): boolean;
  setPlaybackRate(rate: number): void;
  getPlaybackRate(): number;
}

/**
 * Expand long digit sequences (5+ digits) to space-separated digits so TTS
 * reads them one by one (e.g. "15475852" → "1 5 4 7 5 8 5 2").
 * Numbers that are part of decimals (3.14159) or thousands-formatted (1,234,567)
 * are left unchanged since they read naturally as quantities.
 */
function expandLongNumbersForSpeech(text: string): string {
  return text.replace(/(?<![.,\d])(\d{5,})(?![.,\d])/g, (_, digits: string) =>
    digits.split('').join(' '),
  );
}

function stripIconsForTts(text: string): string {
  return text
    .replace(/[\p{Extended_Pictographic}\p{Emoji_Presentation}\u200D\uFE0F]/gu, ' ')
    .replace(/[\u{1F1E6}-\u{1F1FF}\uE000-\uF8FF]/gu, ' ');
}

const GROUPED_NUMBER_SCALES: Record<string, string[]> = {
  en: ['thousand', 'million', 'billion', 'trillion'],
  he: ['אלף', 'מיליון', 'מיליארד', 'טריליון'],
};

function normalizeGroupedNumberPart(group: string): string {
  const normalized = String(Number(group));
  return normalized === 'NaN' ? group : normalized;
}

function convertGroupedNumberToSpeech(integerPart: string, fractionPart: string | undefined, locale: string): string {
  const groups = integerPart.split(',');
  const scaleWords = GROUPED_NUMBER_SCALES[locale] ?? GROUPED_NUMBER_SCALES.en;
  const spokenGroups: string[] = [];

  for (let i = 0; i < groups.length; i += 1) {
    const group = groups[i];
    if (!group || /^0+$/.test(group)) continue;

    const normalizedGroup = normalizeGroupedNumberPart(group);
    const scaleIndex = groups.length - i - 1;
    if (scaleIndex === 0) {
      spokenGroups.push(normalizedGroup);
      continue;
    }

    const scaleWord = scaleWords[scaleIndex - 1];
    spokenGroups.push(scaleWord ? `${normalizedGroup} ${scaleWord}` : normalizedGroup);
  }

  const spokenInteger = spokenGroups.join(' ').trim() || '0';
  if (!fractionPart || /^0+$/.test(fractionPart)) {
    return spokenInteger;
  }

  // Two-digit fractions are usually currency cents in widget replies, and
  // rounding them away tends to sound more natural than reading each digit.
  if (fractionPart.length === 2) {
    return spokenInteger;
  }

  const decimalWord = locale === 'he' ? 'נקודה' : 'point';
  return `${spokenInteger} ${decimalWord} ${fractionPart.split('').join(' ')}`;
}

function convertGroupedNumbersForSpeech(text: string, locale: string): string {
  return text.replace(
    /(?<!\d)(\d{1,3}(?:,\d{3})+)(?:\.(\d+))?(?!\d)/g,
    (_match, integerPart: string, fractionPart?: string) =>
      convertGroupedNumberToSpeech(integerPart, fractionPart, locale),
  );
}

function splitSpeechCommas(text: string): string[] {
  const segments: string[] = [];
  let current = '';

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    const prev = i > 0 ? text[i - 1] : '';
    const next = i < text.length - 1 ? text[i + 1] : '';
    const commaInsideNumber = /\d/.test(prev) && /\d/.test(next);

    if (ch === ',' && !commaInsideNumber) {
      const segment = current.trim();
      if (segment) segments.push(segment);
      current = '';
      continue;
    }

    current += ch;
  }

  const tail = current.trim();
  if (tail) segments.push(tail);
  return segments;
}

function normalizeStructuredLineForSpeech(line: string): string {
  const trimmed = line.trim();
  if (!trimmed) return line;

  const segments = splitSpeechCommas(trimmed);
  if (segments.length < 4) return line;

  const averageSegmentLength = segments.reduce((sum, segment) => sum + segment.length, 0) / segments.length;
  const looksLikeStructuredRecord = /^(?:\d+[.)]|rank\b|row\b|item\b|\u05d3\u05d9\u05e8\u05d5\u05d2\b|\u05e9\u05d5\u05e8\u05d4\b|\u05e4\u05e8\u05d9\u05d8\b)/iu.test(trimmed);
  if (!looksLikeStructuredRecord && averageSegmentLength > 42) return line;

  const [lead, ...rest] = segments;
  const spokenLead = lead.replace(/[.!?;:]+$/u, '').trim();
  const spokenRest = rest
    .map((segment) => segment.replace(/[.!?;:]+$/u, '').trim())
    .filter(Boolean)
    .map((segment) => `${segment}.`)
    .join(' ');

  if (!spokenLead) return spokenRest;
  if (!spokenRest) return `${spokenLead}.`;
  return `${spokenLead}: ${spokenRest}`;
}

function convertStructuredLinesForSpeech(text: string): string {
  return text
    .split(/\r\n?|\n/)
    .map((line) => normalizeStructuredLineForSpeech(line))
    .join('\n');
}

function convertMarkdownTablesToSpeech(text: string): string {
  const lines = text.split(/\r\n?|\n/);
  const result: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();

    if (line.startsWith('|') && line.endsWith('|')) {
      const tableLines: string[] = [lines[i]];
      i += 1;

      while (i < lines.length) {
        const next = lines[i].trim();
        if (!next.startsWith('|') || !next.endsWith('|')) break;
        tableLines.push(lines[i]);
        i += 1;
      }

      const rows = tableLines.map((tableLine) =>
        tableLine.trim().slice(1, -1).split('|').map((cell) => cell.trim()),
      );
      const separatorIndex = rows.findIndex((row) => row.every((cell) => /^[-:]+$/.test(cell)));

      let headers: string[] = [];
      let dataRows: string[][] = [];

      if (separatorIndex === 1) {
        headers = rows[0];
        dataRows = rows.slice(2);
      } else if (separatorIndex > 1) {
        headers = rows[0];
        dataRows = rows.filter((_, index) => index !== 0 && index !== separatorIndex);
      } else {
        dataRows = rows;
      }

      const spokenRows = dataRows
        .filter((row) => row.some((cell) => cell))
        .map((row) => {
          const parts = headers.length > 0
            ? row.map((cell, index) => `${headers[index] ?? ''} ${cell}`.trim())
            : row;
          return parts.join('. ');
        });

      result.push(spokenRows.join('\n'));
      continue;
    }

    result.push(lines[i]);
    i += 1;
  }

  return result.join('\n');
}

function preserveStructuredLinesForTts(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    // Keep ordered-list numbers so speech stays aligned with what the user sees.
    .replace(/(^|\n)[\t ]*(\d+)\.\s+/g, '$1$2. ')
    // Remove bullet markers, but keep each line as its own spoken segment.
    .replace(/(^|\n)[\t ]*[-*+]\s+/g, '$1');
}

function convertLineBreaksToSpeechPauses(text: string): string {
  return text
    .replace(/[ \t]*\n+[ \t]*/g, '. ')
    .replace(/([.!?])(?:\s*[.!?])+\s*/g, '$1 ');
}

/** Strip markdown and formatting for clean speech text */
export function cleanTextForTts(text: string, locale: string): string {
  let s = convertMarkdownTablesToSpeech(text);
  s = preserveStructuredLinesForTts(s);
  s = s.replace(/```[\s\S]*?```/g, ' ');
  s = s.replace(/`([^`]*)`/g, '$1');
  s = s.replace(/!\[[^\]]*\]\([^)]*\)/g, ' ');
  s = s.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1');
  s = s.replace(/^#{1,6}\s*/gm, '');
  s = s.replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1');
  s = s.replace(/_{1,3}([^_]+)_{1,3}/g, '$1');
  s = s.replace(/~~([^~]+)~~/g, '$1');
  s = s.replace(/^>\s*/gm, '');
  s = s.replace(/^[\t ]*[-*+]\s+/gm, '');
  s = s.replace(/^[|\-:\s]+$/gm, '');
  s = s.replace(/\|/g, ' ');
  s = s.replace(/<[^>]+>/g, ' ');
  s = s.replace(/&[a-zA-Z]+;/g, ' ');
  s = s.replace(/https?:\/\/\S+/g, ' ');
  // Normalize YYYY-MM(-DD) date patterns so the hyphen is not read as "until" in Hebrew TTS
  s = s.replace(/\b(\d{4})-(\d{2})(?:-(\d{2}))?\b/g, (_, y, m, d) =>
    d ? `${y} ${m} ${d}` : `${y} ${m}`,
  );
  s = convertGroupedNumbersForSpeech(s, locale);
  s = stripIconsForTts(s);
  s = s.replace(/-{3,}/g, ' ');
  s = convertStructuredLinesForSpeech(s);
  s = convertLineBreaksToSpeechPauses(s);
  s = s.replace(/\s{2,}/g, ' ');
  s = expandLongNumbersForSpeech(s);
  return prepareTextForTts(s, locale);
}

function splitTextForTts(text: string, maxLength = 4000): string[] {
  const parts: string[] = [];
  let remaining = text.trim();

  while (remaining.length > maxLength) {
    let splitAt = remaining.lastIndexOf(' ', maxLength);
    if (splitAt < Math.floor(maxLength * 0.6)) {
      splitAt = maxLength;
    }
    parts.push(remaining.slice(0, splitAt).trim());
    remaining = remaining.slice(splitAt).trim();
  }

  if (remaining) {
    parts.push(remaining);
  }

  return parts;
}

const STREAMING_CHUNK_TARGET_LENGTH = 220;
const STREAMING_CHUNK_MAX_LENGTH = 420;

function takeStreamingChunk(buffer: string): { chunk: string | null; remainder: string } {
  const normalized = buffer.replace(/\r/g, '');

  const paragraphBoundary = normalized.match(/^[\s\S]*?\n{2,}/);
  if (paragraphBoundary?.[0]) {
    return {
      chunk: paragraphBoundary[0].trim(),
      remainder: normalized.slice(paragraphBoundary[0].length).trimStart(),
    };
  }

  const sentencePattern = /[.!?\u2026]+(?:["')\]]*)\s*/g;
  let sentenceMatch: RegExpExecArray | null;
  let targetBoundary = -1;
  let maxBoundary = -1;

  while ((sentenceMatch = sentencePattern.exec(normalized)) !== null) {
    const boundary = sentenceMatch.index + sentenceMatch[0].length;
    if (boundary <= STREAMING_CHUNK_MAX_LENGTH) {
      maxBoundary = boundary;
    }
    if (boundary >= STREAMING_CHUNK_TARGET_LENGTH) {
      targetBoundary = boundary;
      break;
    }
  }

  const splitAtSentence = targetBoundary !== -1
    ? targetBoundary
    : (normalized.length >= STREAMING_CHUNK_MAX_LENGTH ? maxBoundary : -1);

  if (splitAtSentence !== -1) {
    return {
      chunk: normalized.slice(0, splitAtSentence).trim(),
      remainder: normalized.slice(splitAtSentence).trimStart(),
    };
  }

  if (normalized.length >= STREAMING_CHUNK_MAX_LENGTH) {
    const splitAt = normalized.lastIndexOf(' ', STREAMING_CHUNK_MAX_LENGTH);
    if (splitAt >= 80) {
      return {
        chunk: normalized.slice(0, splitAt).trim(),
        remainder: normalized.slice(splitAt).trimStart(),
      };
    }
  }

  return { chunk: null, remainder: normalized };
}

class ServerTtsService implements TtsService {
  private static readonly MAX_PREFETCH = 2;

  private _isSpeaking = false;
  private currentAudio: HTMLAudioElement | null = null;
  private abortControllers = new Set<AbortController>();
  private currentBlobUrl: string | null = null;
  private queue: Array<{ text: string; locale: string }> = [];
  // readyQueue is kept sorted by seq so playback is always in order
  private readyQueue: Array<{ audio: HTMLAudioElement; blobUrl: string; seq: number }> = [];
  private callbacks: TtsCallbacks | null = null;
  private playbackStarted = false;
  private streamActive = false;
  private streamFinished = false;
  private streamLocale = 'he';
  private streamBuffer = '';
  private fetchCount = 0;
  private nextFetchSeq = 0;  // assigned when shifting from queue
  private nextPlaySeq = 0;   // expected seq for next playback
  private sessionId = 0;
  private playbackRate = 1;

  isSupported(): boolean {
    return true;
  }

  speak(text: string, locale: string, callbacks?: TtsCallbacks): void {
    this.stop();

    const cleanText = cleanTextForTts(text, locale);
    if (!cleanText) {
      callbacks?.onEnd?.();
      return;
    }

    this.callbacks = callbacks || null;
    this._isSpeaking = true;
    this.queue = splitTextForTts(cleanText).map(part => ({ text: part, locale }));
    void this.pumpQueue();
  }

  startStreaming(locale: string, callbacks?: TtsCallbacks): void {
    this.stop();
    this.callbacks = callbacks || null;
    this._isSpeaking = true;
    this.streamActive = true;
    this.streamFinished = false;
    this.streamLocale = locale;
    this.streamBuffer = '';
    this.queue = [];
    this.readyQueue = [];
    this.playbackStarted = false;
  }

  appendStreamingText(text: string): void {
    if (!this.streamActive || !text) return;

    this.streamBuffer += text;

    while (true) {
      const { chunk, remainder } = takeStreamingChunk(this.streamBuffer);
      this.streamBuffer = remainder;
      if (!chunk) break;
      this.enqueueChunk(chunk, this.streamLocale);
    }
  }

  finishStreaming(): void {
    if (!this.streamActive) return;

    if (this.streamBuffer.trim()) {
      this.enqueueChunk(this.streamBuffer, this.streamLocale);
      this.streamBuffer = '';
    }

    this.streamFinished = true;
    void this.pumpQueue();
  }

  hasActiveStream(): boolean {
    return this.streamActive;
  }

  stop(): void {
    this.sessionId += 1;
    this._isSpeaking = false;
    this.streamActive = false;
    this.streamFinished = false;
    this.streamBuffer = '';
    this.queue = [];
    this.clearReadyQueue();
    this.callbacks = null;
    this.playbackStarted = false;
    this.fetchCount = 0;
    this.nextFetchSeq = 0;
    this.nextPlaySeq = 0;
    for (const ctrl of this.abortControllers) {
      ctrl.abort();
    }
    this.abortControllers.clear();
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.src = '';
      this.currentAudio = null;
    }
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl);
      this.currentBlobUrl = null;
    }
  }

  isSpeaking(): boolean {
    return this._isSpeaking;
  }

  setPlaybackRate(rate: number): void {
    const normalized = Number.isFinite(rate) ? Math.min(1.6, Math.max(0.7, rate)) : 1;
    this.playbackRate = normalized;

    if (this.currentAudio) {
      this.currentAudio.playbackRate = normalized;
    }

    for (const item of this.readyQueue) {
      item.audio.playbackRate = normalized;
    }
  }

  getPlaybackRate(): number {
    return this.playbackRate;
  }

  private enqueueChunk(text: string, locale: string): void {
    const cleanText = cleanTextForTts(text, locale);
    if (!cleanText) return;

    for (const part of splitTextForTts(cleanText)) {
      this.queue.push({ text: part, locale });
    }

    void this.pumpQueue();
  }

  private async pumpQueue(): Promise<void> {
    this.playNextReadyAudio();

    // Kick off up to MAX_PREFETCH fetches in parallel so the next chunk is
    // already downloaded while the current one is still playing.
    while (this.fetchCount < ServerTtsService.MAX_PREFETCH && this.queue.length > 0) {
      void this.fetchNextChunk();
    }

    this.finalizeIfIdle();
  }

  private async fetchNextChunk(): Promise<void> {
    const next = this.queue.shift();
    const sessionId = this.sessionId;
    if (!next) {
      this.finalizeIfIdle();
      return;
    }

    // Assign a sequence number before going async so playback order is preserved
    // even when two fetches run in parallel.
    const seq = this.nextFetchSeq++;
    this.fetchCount++;
    const ctrl = new AbortController();
    this.abortControllers.add(ctrl);

    try {
      const widgetId = store.getState().config.widgetId;
      const qs = widgetId ? `?widgetId=${encodeURIComponent(widgetId)}` : '';
      const url = `${store.getState().config.serverUrl}/webchat/tts${qs}`;
      const token = store.getState().auth.token;

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          text: next.text,
          language: next.locale,
          ...(store.getState().drivingMode.userGender
            ? { userGender: store.getState().drivingMode.userGender }
            : {}),
        }),
        signal: ctrl.signal,
      });

      if (!res.ok) {
        const errBody = await res.text().catch(() => '');
        throw new Error(`TTS ${res.status}: ${errBody}`);
      }


      const buffer = await res.arrayBuffer();
      if (!this._isSpeaking || !this.isSessionCurrent(sessionId)) return;


      const contentType = res.headers.get('content-type') || 'audio/ogg';
      const blob = new Blob([buffer], { type: contentType });
      const blobUrl = URL.createObjectURL(blob);
      const audio = new Audio(blobUrl);
      await applyAudioOutput(audio);

      audio.playbackRate = this.playbackRate;

      // Insert into readyQueue sorted by seq so out-of-order completions don't
      // cause sentences to play in the wrong order.
      const insertAt = this.readyQueue.findIndex(item => item.seq > seq);
      if (insertAt === -1) {
        this.readyQueue.push({ audio, blobUrl, seq });
      } else {
        this.readyQueue.splice(insertAt, 0, { audio, blobUrl, seq });
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      console.error('[tts] Failed:', err);
      if (!this.isSessionCurrent(sessionId)) return;
      this.handleError(err?.message || 'TTS failed');
      return;
    } finally {
      if (this.isSessionCurrent(sessionId)) {
        this.abortControllers.delete(ctrl);
        this.fetchCount--;
      }
    }

    if (!this.isSessionCurrent(sessionId)) return;
    void this.pumpQueue();
  }

  private playNextReadyAudio(): void {
    if (this.currentAudio) return;

    // Only play the item whose seq matches what we expect next; if it arrived
    // out of order the correct one will arrive soon and pumpQueue will be called.
    const nextReady = this.readyQueue[0];
    if (!nextReady || nextReady.seq !== this.nextPlaySeq) return;
    this.readyQueue.shift();

    const { audio, blobUrl } = nextReady;
    const sessionId = this.sessionId;

    this.currentAudio = audio;
    this.currentBlobUrl = blobUrl;

    audio.onplay = () => {
      if (!this.isSessionCurrent(sessionId)) {
        audio.pause();
        this.releaseBlobUrl(blobUrl);
        return;
      }
      if (!this.playbackStarted) {
        this.playbackStarted = true;
        this.callbacks?.onStart?.();
      }
    };

    audio.onended = () => {
      if (this.currentAudio === audio) {
        this.currentAudio = null;
      }
      this.releaseBlobUrl(blobUrl);
      if (!this.isSessionCurrent(sessionId)) return;
      this.nextPlaySeq++;
      void this.pumpQueue();
    };

    audio.onerror = () => {
      if (this.currentAudio === audio) {
        this.currentAudio = null;
      }
      this.releaseBlobUrl(blobUrl);
      if (!this.isSessionCurrent(sessionId)) return;
      this.nextPlaySeq++;
      this.handleError('Playback failed');
    };

    audio.play().catch((err) => {
      console.error('[tts] Play failed:', err);
      if (this.currentAudio === audio) {
        this.currentAudio = null;
      }
      this.releaseBlobUrl(blobUrl);
      if (!this.isSessionCurrent(sessionId)) return;
      this.nextPlaySeq++;
      this.handleError(err?.message || 'Playback failed');
    });
  }

  private finalizeIfIdle(): void {
    if (this.currentAudio || this.fetchCount > 0 || this.readyQueue.length > 0 || this.queue.length > 0) {
      return;
    }

    if (this.streamActive && this.streamFinished) {
      const callbacks = this.callbacks;
      this._isSpeaking = false;
      this.streamActive = false;
      this.streamFinished = false;
      this.callbacks = null;
      callbacks?.onEnd?.();
    } else if (!this.streamActive && this._isSpeaking) {
      const callbacks = this.callbacks;
      this._isSpeaking = false;
      this.callbacks = null;
      callbacks?.onEnd?.();
    }
  }

  private clearReadyQueue(): void {
    for (const item of this.readyQueue) {
      item.audio.pause();
      item.audio.src = '';
      URL.revokeObjectURL(item.blobUrl);
    }
    this.readyQueue = [];
  }

  private isSessionCurrent(sessionId: number): boolean {
    return sessionId === this.sessionId;
  }

  private releaseBlobUrl(blobUrl: string): void {
    URL.revokeObjectURL(blobUrl);
    if (this.currentBlobUrl === blobUrl) {
      this.currentBlobUrl = null;
    }
  }

  private handleError(message: string): void {
    const callbacks = this.callbacks;
    this.stop();
    callbacks?.onError?.(message);
  }
}

let instance: TtsService | null = null;

export function getTtsService(): TtsService {
  if (!instance) {
    instance = new ServerTtsService();
  }
  return instance;
}
